import { useContext, useEffect, useState } from 'react';
import { MyContext } from '../../context';
import DataTable from '../../components/tables/DataTable';
import StatCard from '../../components/StatCard';
import ExportButton from '../../components/ExportButton';
import Pagination from '../../components/Pagination';
import DateRangePicker from '../../components/DateRangePicker';
import { getCouponUsageReport, listCoupons } from '../../utils/queries';

const PAGE_SIZE = 20;

const formatDiscount = (amount, unit) =>
	unit === 'points' ? `${amount} pts` : `$${Number(amount).toFixed(2)}`;

const FAILED_VALIDATION_STATES = ['VALIDATION_FAILED', 'ERP_FAILED'];

function TestBadge() {
	return (
		<span className='inline-block rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-xs font-medium'>
			TEST
		</span>
	);
}

function FailedValidationBadge() {
	return (
		<span className='inline-block rounded-full bg-red-50 text-pi-red px-2 py-0.5 text-xs font-medium'>
			Failed Validation
		</span>
	);
}

// Used by the commented-out "Unconfirmed" badge in the table below.
// eslint-disable-next-line no-unused-vars
function UnconfirmedBadge() {
	return (
		<span
			className='inline-block rounded-full bg-gray-100 text-gray-500 px-2 py-0.5 text-xs font-medium'
			title='No confirmed coupon redemption record -- inferred from the order as submitted.'
		>
			Unconfirmed
		</span>
	);
}

export default function CouponUsageReport() {
	const { activeStoreId } = useContext(MyContext);
	const [range, setRange] = useState({});
	const [code, setCode] = useState('');
	const [page, setPage] = useState(0);
	const [report, setReport] = useState(null);
	const [loading, setLoading] = useState(true);
	const [coupons, setCoupons] = useState([]);

	useEffect(() => {
		listCoupons({ storeId: activeStoreId || undefined, limit: 200 }).then((result) => {
			if (result?.success) setCoupons(result.data.coupons);
		});
	}, [activeStoreId]);

	const refresh = () => {
		setLoading(true);
		return getCouponUsageReport({
			storeId: activeStoreId || undefined,
			dateFrom: range.from || undefined,
			dateTo: range.to || undefined,
			code: code || undefined,
			limit: PAGE_SIZE,
			offset: page * PAGE_SIZE,
		}).then((result) => {
			if (result?.success) setReport(result.data);
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeStoreId, range.from, range.to, code, page]);

	const filterFrom = (setter) => (e) => {
		setter(e.target.value);
		setPage(0);
	};

	const columns = [
		{
			key: 'coupon_code',
			header: 'Coupon Code',
			render: (value, row) => (
				<span className='inline-flex items-center gap-2'>
					{value?.toUpperCase()}
					{/* {!row.confirmed && <UnconfirmedBadge />} */}
				</span>
			),
		},
		{
			key: 'order_number',
			header: 'Order',
			render: (value, row) => (
				<span className='inline-flex items-center gap-2'>
					{value ?? row.order_id}
					{row.order_mode === 'test' && <TestBadge />}
				</span>
			),
		},
		{
			key: 'discount_amount',
			header: 'Discount',
			render: (value, row) => formatDiscount(value, row.discount_unit),
		},
		{
			key: 'redeemed_at',
			header: 'Redeemed At',
			render: (value) => (value ? new Date(value).toLocaleString() : ''),
		},
		{
			key: 'validation_state',
			header: 'Status',
			render: (value) => (FAILED_VALIDATION_STATES.includes(value) ? <FailedValidationBadge /> : null),
		},
	];

	return (
		<div className='py-8 pr-8'>
			<div className='flex items-center justify-between mb-6'>
				<h1 className='text-2xl font-light tracking-tight'>Coupon Usage Report</h1>
				<ExportButton
					fileName='coupon-usage-report'
					sections={[
						{
							data: report?.rows || [],
							columns: columns.map((c) => c.header),
							columnKeys: columns.map((c) => c.key),
							columnTypes: ['text', 'text', 'text', 'date', 'text'],
						},
					]}
				/>
			</div>

			<div className='flex gap-4 mb-6'>
				<div>
					<label>Coupon Code</label>
					<select className='form-select min-w-36' value={code} onChange={filterFrom(setCode)}>
						<option value=''>All</option>
						{coupons.map((coupon) => (
							<option key={coupon.couponId} value={coupon.code}>
								{coupon.code.toUpperCase()}
							</option>
						))}
					</select>
				</div>
				<div>
					<label>Date Range</label>
					<DateRangePicker
						value={range}
						onChange={(item) => {
							setRange(item);
							setPage(0);
						}}
						className='w-72'
					/>
				</div>
			</div>

			{loading || !report ? (
				<div className='text-sm text-gray-400'>Loading...</div>
			) : (
				<>
					<div className='grid grid-cols-5 gap-4 mb-8'>
						<StatCard label='Total Redemptions' value={report.summary.total_redemptions} />
						<StatCard
							label='Total Discount ($)'
							value={`$${Number(report.summary.total_discount_currency).toFixed(2)}`}
						/>
						<StatCard label='Total Discount (pts)' value={report.summary.total_discount_points} />
						<StatCard label='Unique Coupons Used' value={report.summary.unique_coupons_used} />
						<StatCard label='Unconfirmed' value={report.summary.unconfirmed_count} />
					</div>

					<DataTable
						columns={columns}
						data={report.rows}
						loading={loading}
						emptyHeading='No coupon redemptions in range'
					/>
					<Pagination
						pageCount={Math.ceil((report.pagination?.total || 0) / PAGE_SIZE)}
						currentPage={page}
						onPageChange={setPage}
					/>
				</>
			)}
		</div>
	);
}
